/**
 * Credit Service
 * 
 * Provides functions to check user's credit balance on the main website.
 */

import { APP_CONFIG } from '../configs/config';
import { authenticatedFetch } from '../utils/authCookieHelpers';

/**
 * Get the current user's credit balance from the main website API.
 * 
 * @returns {Promise<Object>} - Object containing balance
 * @throws {Error} - If request fails or user is not authenticated
 */
export const getCreditBalance = async () => {
  try {
    const response = await authenticatedFetch(
      `${APP_CONFIG.MAIN_WEBSITE_API_URL}/payment/me/credits`,
      {
        method: 'GET'
      }
    );

    if (!response.ok) {
      let message = `Failed to fetch credit balance: ${response.status}`;
      try {
        const data = await response.json();
        message = data?.detail || message;
      } catch (error) {
        /* ignore json parsing errors */
      }
      throw new Error(message);
    }

    const data = await response.json();
    return {
      balance: data.balance || 0
    };
  } catch (error) {
    console.error('[CreditService] Error fetching credit balance:', error);
    throw error;
  }
};

/**
 * Check if user has sufficient credits.
 * 
 * @returns {Promise<Object>} - Object with hasCredits boolean and balance
 * @throws {Error} - If request fails
 */
export const checkSufficientCredits = async () => {
  try {
    const { balance } = await getCreditBalance();
    return {
      hasCredits: balance >= 0,
      balance
    };
  } catch (error) {
    console.error(
      '[CreditService] Error checking sufficient credits:',
      error
    );
    throw error;
  }
};

